import { List, Map } from 'immutable'
import _includes from 'lodash/includes'
import { US, TAXABLE_COUNTRIES } from 'services/country'
import 'store/types'
import { GIFT_SUBSCRIPTION_STATE } from 'services/gift'
import { getAuthIsLoggedIn } from 'services/auth'
import { isEntitled } from 'services/subscription'
// eslint-disable-next-line import/no-cycle
import {
  USER_ACCOUNT_ACTIVE,
  USER_ACCOUNT_ANONYMOUS,
  USER_ACCOUNT_UNENTITLED,
  ACCOUNT_TYPE_ZUORA,
} from '.'

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export function selectTaxMessaging (state) {
  const {
    auth = Map(),
    userAccount = Map(),
    user = Map(),
  } = state
  const userRequestCountry = user.getIn(['data', 'requestCountry'])
  const authCountry = auth.get('country')
  const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
  const country = userAccountCountry || authCountry || userRequestCountry || US

  // country is a taxable country
  return _includes(TAXABLE_COUNTRIES, country)
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectHasGiftSubscription (state) {
  const { userAccount = Map() } = state
  return !!userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'giftStartDate'])
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectHasActiveGiftSubscription (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'gift', 'status']) === GIFT_SUBSCRIPTION_STATE.ACTIVE
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectCurrentGiftPlanId (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'gift', 'currentGift', 'planId'])
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectNextGiftPlanId (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'gift', 'nextGift', 'planId'])
}

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export function selectFutureGiftPlanIds (state) {
  const { userAccount = Map() } = state
  const futureGifts = userAccount.getIn(['details', 'data', 'gift', 'futureGifts']) || List()
  return futureGifts.map((fg) => fg.get('planId'))
}

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export function selectAllGiftPlanIds (state) {
  const { userAccount = Map() } = state
  const currentGiftId = selectCurrentGiftPlanId(state)
  const nextGiftId = selectNextGiftPlanId(state)
  const futureGifts = userAccount.getIn(['details', 'data', 'gift', 'futureGifts']) || List()
  const futureGiftPlanIds = futureGifts.map((fg) => fg.get('planId'))
  const giftPlanIds = List([currentGiftId, nextGiftId, ...futureGiftPlanIds])
  return giftPlanIds.filter((id) => !!id)
}

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export function selectGiftSummary (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'gift'], undefined)
}

/**
 * @param {ReduxState} state redux state
 * @returns {List}
 */
export function selectGiftPaidThroughDate (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'gift', 'paidThroughDate'], undefined)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export function selectAccountStatus (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
}

export function selectBillingAccountId (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
}

export function selectNextPlan (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan'])
}

export function selectEndDate (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
}

export function selectProductRatePlanId (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
}

export function selectPaidThroughDate (state) {
  const { userAccount = Map() } = state
  return userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
}

export function selectNextProductRatePlanId (state) {
  const nextPlan = selectNextPlan(state)
  return Map.isMap(nextPlan) && nextPlan.size > 0 ? nextPlan.get('productRatePlanId') : null
}

export function selectUserAccount (state) {
  const { userAccount = Map() } = state
  return userAccount
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectMembershipStatus (state) {
  const { auth, userAccount } = state
  // default to active if the account data is not available
  const accountStatus = selectAccountStatus(state) || USER_ACCOUNT_ACTIVE
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const subscriptions = auth.get('subscriptions', List())
  const userIsEntitled = !!isEntitled(subscriptions)

  // If user is not logged in, return anonymous status
  if (!isLoggedIn) {
    return USER_ACCOUNT_ANONYMOUS
  }

  // Handle non-Zuora accounts
  const accountType = userAccount.get('accountType')
  if (accountType !== ACCOUNT_TYPE_ZUORA) {
    return accountStatus === '400' && userIsEntitled ? USER_ACCOUNT_ACTIVE : accountStatus
  }

  // Handle Zuora accounts - check if user is entitled to content
  return userIsEntitled ? accountStatus : USER_ACCOUNT_UNENTITLED
}

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export function selectGuestPhase (state) {
  const { userAccount, paytrack } = state
  const membershipStatus = selectMembershipStatus(state)
  const accountType = userAccount.get('accountType')

  if (accountType !== ACCOUNT_TYPE_ZUORA) {
    const offerDiscountType = paytrack.getIn(['lastTransaction', 'provider_data', 'offerDiscountType']) || ''
    return offerDiscountType === 'FREE_TRIAL'
  }

  // set to false if the billing data is not available
  const inTrialSegment = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'inTrialSegment']) || false
  const isDefaultGuestPhase = membershipStatus === USER_ACCOUNT_ANONYMOUS
    || membershipStatus === USER_ACCOUNT_UNENTITLED

  return isDefaultGuestPhase ? false : inTrialSegment
}
